/**
 * PageTransition Component
 * 
 * Direction-aware route transitions:
 * - Drill-down routes (e.g. /projects/) slide in from the right.
 * - Top-level space switches use a subtle fade.
 * Respects prefers-reduced-motion preference.
 * 
 * Requirements: 21.10, 19.5, Phase 2 — Native Feel
 */

import * as React from 'react';
import { motion } from 'framer-motion';
import { useRouterState } from '@tanstack/react-router';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface PageTransitionProps {
  children: React.ReactNode;
}

/** Routes that get a slide-in transition (drill-down into detail). */
const DRILL_DOWN_PREFIXES = ['/projects/', '/knowledge/'];

export const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const prefersReducedMotion = useReducedMotion();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isDrillDown = DRILL_DOWN_PREFIXES.some((p) => pathname.startsWith(p));

  const enterX = prefersReducedMotion ? 0 : isDrillDown ? 24 : 0;
  const enterY = prefersReducedMotion ? 0 : isDrillDown ? 0 : 10;

  return (
    <motion.div
      initial={{
        opacity: prefersReducedMotion ? 1 : 0,
        x: enterX,
        y: enterY,
      }}
      animate={{
        opacity: 1,
        x: 0,
        y: 0,
      }}
      exit={{
        opacity: prefersReducedMotion ? 1 : 0,
        x: isDrillDown ? -enterX : 0,
        y: isDrillDown ? 0 : -10,
      }}
      transition={{
        duration: prefersReducedMotion ? 0 : 0.22,
        ease: 'easeInOut',
      }}
      className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto"
    >
      {children}
    </motion.div>
  );
};
